import type {
  ProductLastPurchaseInfo,
  Translations,
  Product,
  ProductBulkPrice,
  ProductModularizedDescriptionBlock,
  StoreType,
} from '@frodo/models/types';
import { formatIsoDate } from '@frodo/lib/utils/common/stringUtils';
import { deobfuscateNumber } from '@frodo/lib/utils/common/obfuscation';

// Config SKUs are prefixed with "c" (e.g. c12345); their product query may resolve to a different SKU.
export const isConfigSku = (sku: string): boolean => sku.startsWith('c');

export const hasBundleComponents = (
  product: Product,
): product is Product & { variety: 'bundle'; bundleComponents: Product[] } =>
  product.variety.toLowerCase() === 'bundle' &&
  Array.isArray(product.bundleComponents) &&
  product.bundleComponents.length > 0;

export const isDeliveryScopeBlock = (
  block: ProductModularizedDescriptionBlock,
  deliveryScopeLabel: string,
): boolean =>
  block.label?.trim().toLowerCase() === deliveryScopeLabel.trim().toLowerCase();

/**
 * A product is unavailable for purchase when it is deactivated, not sellable
 * (no merchant offer, no price, ERP-blocked), or out of stock. All fields are
 * optional and independently sourced, so only an explicit signal counts —
 * `isActive: false`, `isSellable: false`, `hasStock: false`, or a decoded
 * stock quantity of 0 or less. Absent fields mean available.
 */
export const isProductUnavailable = ({
  isActive,
  isSellable,
  hasStock,
  _t,
}: Product): boolean => {
  const stock = deobfuscateNumber(_t);

  return (
    isActive === false ||
    isSellable === false ||
    hasStock === false ||
    (typeof stock === 'number' && stock <= 0)
  );
};

export const getLastPurchasedTimeText = (
  item: ProductLastPurchaseInfo | undefined | null,
  trans: Translations,
  store: StoreType,
): string => {
  if (!item?.lastPurchasedDate) {
    return '';
  }

  const formattedDate = formatIsoDate(item.lastPurchasedDate, store);

  if (!formattedDate || formattedDate.includes('Invalid')) {
    return '';
  }

  return `${trans['product.lastPurchased']} ${formattedDate}`;
};
export const getHighestQuantityBulkPrice = (
  bulkPriceInfo: ProductBulkPrice[],
): ProductBulkPrice =>
  bulkPriceInfo.reduce((highest, tier) =>
    tier.quantity > highest.quantity ? tier : highest,
  );
